import React, { useEffect } from "react";
import { Box, Button } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import ActionToast from "../../../../shared/components/ActionToast";
import {
  getAccount,
  clearError,
  updateAccount,
  setOpenSnackbar,
} from "../../customerAccountSlice";
import { updateUser } from "../../../../account/accountSlice";
import ChangeAccountPasswordDialogue from "../ChangeAccountPasswordDialogue";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { UpdateAccount } from "../../../shared/models/customerModels";
import { phoneRegex } from "../../../../shared/validators";
import SettingsCard from "../shared/SettingsCard";
import SettingsFormField from "../shared/SettingsFormField";
import Loader from "../../../../shared/components/Loader";
import { LockOutlined } from "@mui/icons-material";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../../shared/themes/redesignTokens";

const SettingsProfile: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<UpdateAccount>();

  const dispatch = useAppDispatch();

  const {
    isLoading,
    isSaving,
    userInfo,
    openSnackbar,
    alertMessage,
    severity,
  } = useAppSelector((state) => state.customerAccount);

  const { translate: tAccount } = useTranslation("account");
  const { translate: tSettings } = useTranslation("settings");

  const watchedFields = watch(["firstName", "lastName", "phoneNumber", "department"]);

  const isFormValid =
    watchedFields[0] &&
    String(watchedFields[0]).trim() !== "" &&
    watchedFields[1] &&
    String(watchedFields[1]).trim() !== "" &&
    (!watchedFields[2] || phoneRegex.test(String(watchedFields[2])));

  const handleSaveChanges = async (model: UpdateAccount) => {
    await dispatch(updateAccount(model));
    dispatch(updateUser({ phoneNumber: model.phoneNumber }));
  };

  const onSubmit: SubmitHandler<UpdateAccount> = (model: UpdateAccount) => {
    handleSaveChanges(model);
  };

  const [isChangePasswordOpen, setIsChangePasswordOpen] = React.useState(false);

  const disableSaveButton =
    isSaving || !isFormValid || !isDirty || Object.keys(errors).length > 0;

  useEffect(() => {
    dispatch(getAccount());
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (userInfo) {
      reset({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        phoneNumber: userInfo.phoneNumber,
        department: userInfo.department,
      });
    }
  }, [userInfo, reset]);

  if (isLoading) {
    return <Loader />;
  }

  const saveButton = (
    <Button
      form="settingsProfileForm"
      type="submit"
      variant="contained"
      disabled={disableSaveButton}
      sx={{
        background: disableSaveButton
          ? undefined
          : accentGradientDark,
        borderRadius: dt.radiusPill,
        padding: "8px 24px",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "13px",
        fontFamily: dt.font,
        boxShadow: disableSaveButton ? "none" : "0 2px 8px rgba(52,152,219,0.25)",
        "&:hover": {
          background: accentGradientDarkHover,
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(52,152,219,0.3)",
        },
      }}
    >
      {isSaving ? tAccount("saving") : tAccount("saveChanges")}
    </Button>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <ActionToast
        open={openSnackbar}
        variant={severity}
        message={alertMessage ? tAccount(alertMessage) : ""}
        onClose={() => dispatch(setOpenSnackbar(false))}
        duration={6000}
      />

      {/* Personal Information Card */}
      <SettingsCard
        title={tSettings("settings:profile.title")}
        action={saveButton}
        animationDelay="0.15s"
      >
        <Box
          component="form"
          id="settingsProfileForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Avatar placeholder */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mb: 3 }}>
            <Box
              sx={{
                width: "64px",
                height: "64px",
                minWidth: "64px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${dt.blue}, ${dt.blueHover})`,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "22px",
                fontFamily: dt.font,
              }}
            >
              {userInfo
                ? `${(userInfo.firstName || "").charAt(0)}${(userInfo.lastName || "").charAt(0)}`.toUpperCase()
                : "?"}
            </Box>
            <Box>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: dt.textPrimary,
                  fontFamily: dt.font,
                }}
              >
                {userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : ""}
              </Box>
              <Box
                sx={{
                  fontSize: "12px",
                  color: dt.textMuted,
                  fontFamily: dt.font,
                  mt: 0.25,
                }}
              >
                {userInfo?.email || ""}
              </Box>
            </Box>
          </Box>

          {/* Form fields */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: "16px",
            }}
          >
            <SettingsFormField label={tAccount("firstName")}>
              <Box
                component="input"
                {...register("firstName", { required: tAccount("fieldRequired") })}
                placeholder={tAccount("firstNamePlaceholder")}
                sx={{
                  width: "100%",
                  padding: "10px 14px",
                  border: errors.firstName ? "1.5px solid #e74c3c" : `1.5px solid #E5E7EB`,
                  borderRadius: dt.radiusSm,
                  backgroundColor: dt.inputBg,
                  fontFamily: dt.font,
                  fontSize: "14px",
                  color: dt.textPrimary,
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  "&:focus": {
                    borderColor: dt.blue,
                    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                  },
                }}
              />
              {errors.firstName && (
                <Box sx={{ fontSize: "11px", color: "#e74c3c", fontFamily: dt.font, mt: "4px" }}>
                  {errors.firstName.message}
                </Box>
              )}
            </SettingsFormField>

            <SettingsFormField label={tAccount("lastName")}>
              <Box
                component="input"
                {...register("lastName", { required: tAccount("fieldRequired") })}
                placeholder={tAccount("lastNamePlaceholder")}
                sx={{
                  width: "100%",
                  padding: "10px 14px",
                  border: errors.lastName ? "1.5px solid #e74c3c" : `1.5px solid #E5E7EB`,
                  borderRadius: dt.radiusSm,
                  backgroundColor: dt.inputBg,
                  fontFamily: dt.font,
                  fontSize: "14px",
                  color: dt.textPrimary,
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  "&:focus": {
                    borderColor: dt.blue,
                    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                  },
                }}
              />
              {errors.lastName && (
                <Box sx={{ fontSize: "11px", color: "#e74c3c", fontFamily: dt.font, mt: "4px" }}>
                  {errors.lastName.message}
                </Box>
              )}
            </SettingsFormField>

            <SettingsFormField label={tAccount("email")} readOnly>
              <Box
                component="input"
                disabled
                value={userInfo?.email || ""}
                sx={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1.5px solid #E5E7EB",
                  borderRadius: dt.radiusSm,
                  backgroundColor: "#F9FAFB",
                  fontFamily: dt.font,
                  fontSize: "14px",
                  color: dt.textMuted,
                  outline: "none",
                  boxSizing: "border-box",
                  cursor: "not-allowed",
                }}
              />
            </SettingsFormField>

            <SettingsFormField label={tAccount("phoneNumber")}>
              <Box
                component="input"
                {...register("phoneNumber", {
                  pattern: {
                    value: phoneRegex,
                    message: tAccount("invalidPhoneNumber"),
                  },
                })}
                placeholder={tAccount("phoneNumberPlaceholder")}
                sx={{
                  width: "100%",
                  padding: "10px 14px",
                  border: errors.phoneNumber ? "1.5px solid #e74c3c" : `1.5px solid #E5E7EB`,
                  borderRadius: dt.radiusSm,
                  backgroundColor: dt.inputBg,
                  fontFamily: dt.font,
                  fontSize: "14px",
                  color: dt.textPrimary,
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  "&:focus": {
                    borderColor: dt.blue,
                    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                  },
                }}
              />
              {errors.phoneNumber && (
                <Box sx={{ fontSize: "11px", color: "#e74c3c", fontFamily: dt.font, mt: "4px" }}>
                  {errors.phoneNumber.message}
                </Box>
              )}
            </SettingsFormField>

            <SettingsFormField label={tAccount("department")}>
              <Box
                component="input"
                {...register("department")}
                placeholder={tAccount("departmentPlaceholder")}
                sx={{
                  width: "100%",
                  padding: "10px 14px",
                  border: `1.5px solid #E5E7EB`,
                  borderRadius: dt.radiusSm,
                  backgroundColor: dt.inputBg,
                  fontFamily: dt.font,
                  fontSize: "14px",
                  color: dt.textPrimary,
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  "&:focus": {
                    borderColor: dt.blue,
                    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                  },
                }}
              />
            </SettingsFormField>

            <SettingsFormField label={tAccount("company")} readOnly>
              <Box
                component="input"
                disabled
                value={userInfo?.companyName || ""}
                sx={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1.5px solid #E5E7EB",
                  borderRadius: dt.radiusSm,
                  backgroundColor: "#F9FAFB",
                  fontFamily: dt.font,
                  fontSize: "14px",
                  color: dt.textMuted,
                  outline: "none",
                  boxSizing: "border-box",
                  cursor: "not-allowed",
                }}
              />
            </SettingsFormField>
          </Box>
        </Box>
      </SettingsCard>

      {/* Security Card */}
      <SettingsCard title={tSettings("settings:profile.security")} animationDelay="0.25s">
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Password row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box>
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1.2px",
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                  mb: "4px",
                }}
              >
                {tSettings("settings:profile.passwordLabel")}
              </Box>
              <Box
                sx={{
                  fontSize: "14px",
                  color: dt.textMuted,
                  fontFamily: dt.font,
                  letterSpacing: "2px",
                }}
              >
                {tSettings("settings:profile.passwordMask")}
              </Box>
            </Box>
            <Button
              variant="outlined"
              onClick={() => setIsChangePasswordOpen(true)}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: dt.radiusSm,
                padding: "6px 16px",
                color: dt.textSecondary,
                borderColor: "#E5E7EB",
                "&:hover": {
                  borderColor: dt.blue,
                  color: dt.blue,
                  backgroundColor: "rgba(52,152,219,0.06)",
                },
              }}
            >
              {tSettings("settings:profile.changePassword")}
            </Button>
          </Box>

          {/* Future: 2FA */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pt: 1,
              borderTop: "1px solid #F3F4F6",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <LockOutlined sx={{ fontSize: "18px", color: dt.textMuted }} />
              <Box sx={{ fontSize: "14px", color: dt.textSecondary, fontFamily: dt.font }}>
                {tSettings("settings:profile.twoFactor")}
              </Box>
            </Box>
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: dt.textMuted,
                fontFamily: dt.font,
                backgroundColor: "#F4F6F9",
                padding: "3px 10px",
                borderRadius: "10px",
              }}
            >
              {tSettings("settings:profile.comingSoon")}
            </Box>
          </Box>
        </Box>
      </SettingsCard>

      <ChangeAccountPasswordDialogue
        open={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </Box>
  );
};

export default SettingsProfile;
